import { motion } from "framer-motion";
import { BookOpen, ArrowUpRight, Quote } from "lucide-react";
import { IMAGES } from "@/lib/images";

export default function BookShowcase({ content }) {
  return (
    <section id="book" className="relative py-28 lg:py-36 bg-[#151821]/40 grain" data-testid="book-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -32 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="absolute -inset-8 bg-amber-500/10 blur-[100px] rounded-full pointer-events-none" />
          <div className="relative">
            <div className="absolute -inset-3 border border-amber-500/30 spotlight-frame pointer-events-none" />
            <img
              src={IMAGES.bookMockup}
              alt={`${content.book_title} — the book behind the program`}
              className="relative w-full aspect-[4/5] object-cover spotlight-frame"
              data-testid="book-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E11]/80 via-transparent to-transparent spotlight-frame pointer-events-none" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="font-display font-extrabold text-xl sm:text-2xl text-[#F3F4F6] leading-tight" data-testid="book-cover-title">
                {content.book_title}
              </div>
              <div className="mt-1 font-mono-tactical text-[11px] tracking-[0.2em] text-amber-500">BY ANGEL GARCIA</div>
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="font-mono-tactical text-xs uppercase tracking-[0.25em] text-amber-500"
          >
            The Book Behind The Program
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-5 font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#F3F4F6]"
            data-testid="book-heading"
          >
            {content.book_title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 text-base sm:text-lg text-[#9CA3AF] leading-relaxed max-w-2xl"
            data-testid="book-blurb"
          >
            {content.book_blurb}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 flex items-start gap-4 bg-[#0D0E11] border border-white/10 rounded-xl p-6 max-w-2xl"
          >
            <Quote size={22} className="text-amber-500 shrink-0 mt-1" />
            <p className="text-sm sm:text-base text-[#9CA3AF] leading-relaxed italic">
              Every module in the S2S program maps to a chapter — so facilitators and residents can go deeper between sessions.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <a
              href={content.amazon_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0D0E11] font-bold px-7 py-3.5 rounded-full transition-all hover:gap-3"
              data-testid="book-amazon-btn"
            >
              <BookOpen size={18} /> Buy on Amazon
            </a>
            <a
              href="https://beacons.ai/4rmdastreets2strategist"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-amber-500/50 text-[#F3F4F6] hover:text-amber-500 px-7 py-3.5 rounded-full transition-colors"
              data-testid="book-beacons-btn"
            >
              All Links & Updates <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
